import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { FileText, ArrowRight, Shield, Smartphone, Lock, Car, Home, Heart, Activity } from 'lucide-react'
import { fetchTemplates } from '../utils/api'
import type { Template } from '../types'

const categoryIcons: Record<string, React.ReactNode> = {
  'Automóvel': <Car className="w-6 h-6 text-primary-600" />,
  'Habitação': <Home className="w-6 h-6 text-primary-600" />,
  'Vida': <Heart className="w-6 h-6 text-primary-600" />,
  'Saúde': <Activity className="w-6 h-6 text-primary-600" />,
}

export function HomePage() {
  const [templates, setTemplates] = useState<Template[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchTemplates()
      .then(setTemplates)
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  const groupedTemplates = templates.reduce((acc, template) => {
    const category = template.category || 'Outros'
    if (!acc[category]) acc[category] = []
    acc[category].push(template)
    return acc
  }, {} as Record<string, Template[]>)

  return (
    <div className="flex-1 flex flex-col">
      <section className="bg-gradient-to-br from-primary-600 to-primary-800 text-white py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            Resolução de Contratos de Seguro
          </h1>
          <p className="text-primary-100 text-lg mb-8 max-w-2xl mx-auto">
            Gere e assine documentos de resolução de contrato diretamente no seu dispositivo. Os seus dados pessoais nunca saem do seu telemóvel.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <div className="flex items-center gap-2 bg-white/10 rounded-full px-4 py-2">
              <Shield className="w-5 h-5" />
              <span className="text-sm">100% Privado</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 rounded-full px-4 py-2">
              <Smartphone className="w-5 h-5" />
              <span className="text-sm">Mobile-First</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 rounded-full px-4 py-2">
              <Lock className="w-5 h-5" />
              <span className="text-sm">Processamento Local</span>
            </div>
          </div>
        </div>
      </section>

      <section className="flex-1 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            Modelos de Resolução Disponíveis
          </h2>

          {loading ? (
            <div className="space-y-4">
              {[1, 2].map((i) => (
                <div key={i} className="card p-6 animate-pulse">
                  <div className="h-5 bg-gray-200 rounded w-1/3 mb-3" />
                  <div className="h-4 bg-gray-100 rounded w-2/3" />
                </div>
              ))}
            </div>
          ) : templates.length === 0 ? (
            <div className="card p-8 text-center">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                Nenhum modelo de resolução disponível de momento.
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {Object.entries(groupedTemplates).map(([category, categoryTemplates]) => (
                <div key={category}>
                  <h3 className="text-lg font-medium text-gray-700 mb-4 flex items-center gap-2">
                    {categoryIcons[category] || <FileText className="w-5 h-5 text-primary-600" />}
                    {category}
                  </h3>
                  <div className="space-y-3">
                    {categoryTemplates.map((template) => (
                      <Link
                        key={template.id}
                        to={`/sign/${template.id}`}
                        className="card p-5 flex items-center justify-between hover:shadow-md transition-shadow group"
                      >
                        <div>
                          <h4 className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                            {template.name}
                          </h4>
                          {template.description && (
                            <p className="text-sm text-gray-500 mt-1">
                              {template.description}
                            </p>
                          )}
                          <p className="text-xs text-gray-400 mt-2">
                            {template.fields.filter(f => f.required).length} campos obrigatórios
                          </p>
                        </div>
                        <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-primary-600 transition-colors flex-shrink-0" />
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  )
}
